import React from 'react';
import { initializeApp } from '@firebase/app';
import { getFirestore, doc, writeBatch, getDoc, setDoc } from '@firebase/firestore';
import { getAuth } from "firebase/auth";
import { toastPromise } from '../Helpers/ToastMessage';
import { getFunctions, httpsCallable } from "firebase/functions";
import Bubble from '../Helpers/Bubble';
import 'react-image-upload/dist/index.css';
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { BeatLoader } from 'react-spinners';

import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  FormikEmailInput,
  FormikPasswordInput,
  FormikSelect,
  FormikTextInput,
  FormikCheckBoxSingle,
  FormikSliderNumber
} from '../FormikFields';

const app = initializeApp({
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
});

const db = getFirestore(app);
const functions = getFunctions();
const storage = getStorage(app, process.env.REACT_APP_FIREBASE_STORAGE_BUCKET);
const sendCompanyInviteEmail = httpsCallable(functions, "sendCompanyInviteEmail");
const cloneBaseFormsToCompany = httpsCallable(functions, "cloneBaseFormsToCompany");
const finalizeHighLevelIntegration = httpsCallable(functions, 'finalizeHighLevelIntegration');

function SystemCompanyOptions(props) {
  const [companyOptions, setCompanyOptions] = React.useState();
  const [logo, setLogo] = React.useState();
  const [finalizing, setFinalizing] = React.useState(false);
  
  const OptionsSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email'),
    emailKey: Yup.string(),
    emailProvider: Yup.string(),
    emailReply: Yup.string(),
    emailChecklistStyle: Yup.string().required('Required'),
    logoPath: Yup.string(),
    primaryColor: Yup.string(),
    secondaryColor: Yup.string(),
    tertiaryColor: Yup.string(),
    interpolationData: Yup.object().shape({
        CompanyName: Yup.string(),
        CompanyEmail: Yup.string().email('Invalid email'),
        CompanyWebsite: Yup.string(),
        CompanyPortal: Yup.string(),
        CompanyPhone: Yup.string(),
        CompanyAddress: Yup.string()
    }),
    extraFeatures: Yup.object().shape({
      sharableStatus: Yup.boolean(),
      logoSize: Yup.number().min(150).max(500).integer("Must be an integer"),
      assignAdmins: Yup.boolean(),
      submissionTagging: Yup.boolean(),
      messaging: Yup.boolean(),
      useEmailSignature: Yup.boolean(),
      emailSignature: Yup.string()
    }),
    highLevel: Yup.object().shape({
      enabled: Yup.boolean(),
      locationId: Yup.string(),
      privateIntegrationKey: Yup.string()
    }),
    isSubscribed: Yup.boolean(),
    stripeSubscription: Yup.string(),
    stripeCustomerId: Yup.string(),
    stripeSubscriptionStatus: Yup.string()
  });

  React.useEffect(() => {
    if (props.company && !props.createCompany) {
      getCompanyOptions();
    }
  }, []);

  function getCompanyOptions() {
    console.log("Getting Company Data" + props.company);
    toastPromise(async () => {
      var c = await getDoc(doc(db, `/BusinessRules/${props.company}`));
      
      if (c && c.exists()) {
        var data = c.data();
        
        console.log("Got Data:", data);

        const co = {
            code: props.company,
            email: data.email ?? '',
            emailKey: data.emailKey ?? '',
            emailProvider: data.emailProvider ?? 'gmail',
            emailReply: data.emailReply ?? '',
            name: data.name ?? '',
            emailChecklistStyle: data.emailChecklistStyle ?? '',
            logoPath: data.logoPath ?? '',
            primaryColor: data.primaryColor ?? '',
            secondaryColor: data.secondaryColor ?? '',
            tertiaryColor: data.tertiaryColor ?? '',
            stripeCustomerId: data.stripeCustomerId ?? '',
            yearlyAllotmentProgress: data.yearlyAllotmentProgress ?? 0,
            stripeSubscription: data.stripeSubscription ?? '',
            messagingSubscription: data.messagingSubscription ?? '',
            stripeSubscriptionStatus: data.stripeSubscriptionStatus ?? '',
            isSubscribed: data.isSubscribed ?? false,
            interpolationData: {
              CompanyName: data.interpolationData?.CompanyName ?? '',
              CompanyEmail: data.interpolationData?.CompanyEmail ?? '',
              CompanyWebsite: data.interpolationData?.CompanyWebsite ?? '',
              CompanyPortal: data.interpolationData?.CompanyPortal ?? '',
              CompanyPhone: data.interpolationData?.CompanyPhone ?? '',
              CompanyAddress: data.interpolationData?.CompanyAddress ?? ''
            },
            freeTrial: {
              active: data.freeTrial?.active ?? false,
              limit: data.freeTrial?.limit ?? 0,
              usage: data.freeTrial?.usage ?? 0
            },
            extraFeatures: {
              sharableStatus: data.extraFeatures?.sharableStatus ?? false,
              logoSize: data.extraFeatures?.logoSize ?? 150,
              assignAdmins: data.extraFeatures?.assignAdmins ?? false,
              submissionTagging: data.extraFeatures?.submissionTagging ?? false,
              messaging: data.extraFeatures?.messaging ?? false,
              useEmailSignature: data.extraFeatures?.useEmailSignature ?? false,
              emailSignature: data.extraFeatures?.emailSignature ?? ""
            },
            twilio: {
              sid: data.twilio?.sid ?? "",
              phoneNumber: data.twilio?.phoneNumber ?? "",
              verified: data.twilio?.verified ?? false,
              verificationStatus: data.twilio?.verificationStatus ?? "",
              verificationDescription: data.twilio?.verificationDescription ?? ""
            },
            highLevel: {
              enabled: data.highLevel?.enabled ?? false,
              locationId: data.highLevel?.locationId ?? "",
              privateIntegrationKey: data.highLevel?.privateIntegrationKey ?? ""
            }
        }
        
        setCompanyOptions(co);
      }
    }, true);
  }

  async function uploadFiles() {
    if (!logo || !logo.file || !logo.file.name)
      return null;
    
    const filename = `${companyOptions.code}/Logo/${logo.file.name}`;

    if (companyOptions.logoPath != filename) {   
      try {
        const logoRef = ref(storage, filename);
        const snapshot = await uploadBytes(logoRef, logo.file);

        return filename;
      } catch (e) {
        console.log(e);
        return null;
      }
    }

    return null;
  }

  async function finalizeIntegration(values) {
    setFinalizing(true);
    try {
      await finalizeHighLevelIntegration({
        companyCode: companyOptions.code,
        locationId: values.highLevel.locationId,
        privateIntegrationKey: values.highLevel.privateIntegrationKey
      });

      setCompanyOptions(values);
      saveOptions(values);
      alert('Integration finalized successfully!');
    } catch (error) {
      console.error('Error finalizing integration:', error);
      alert('Error finalizing integration. Please try again.');
    } finally {
      setFinalizing(false);
    }
  }

  function saveForm(values) {
    if (props.createCompany) {
      createCompany(values);
    } else {
      saveOptions(values);
    }
  }

  function createCompany(companyOptions) {
    if (!companyOptions.code)
      return;
    
    toastPromise(async () => {
      var user = getAuth().currentUser;
      var batch = writeBatch(db);
      
      batch.set(doc(db, `/Companies/${companyOptions.code}`), { active: true });
      batch.set(doc(db, `/BusinessRules/${companyOptions.code}`), companyOptions);
      batch.set(doc(db, `/BusinessRules/${companyOptions.code}/Users/${user.uid}`),
        {
          Name: user.displayName,
          Email: user.email,
          Timestamp: new Date()
        }
      );

      await batch.commit();

      if (companyOptions.emailInvitations) {
        var addresses = companyOptions.emailInvitations.join(";");
        sendCompanyInviteEmail({
          company: companyOptions,
          emailAddresses: addresses,
          id: companyOptions.code
        });
      }

      cloneBaseFormsToCompany({
        id: companyOptions.code
      });

      props.finished();
    });
  }

  function saveOptions(companyOptions) {
    toastPromise(async () => {
      var data = { ...companyOptions };
      var uploadedFile = await uploadFiles();

      if (uploadedFile)
        data.logoPath = uploadedFile;

      setDoc(doc(db, `/BusinessRules/${companyOptions.code}`), data)
        .then((v) => {
          props.finished();
        });
    });
  }

  function render() {
    if (!companyOptions) {
      return (<div className="loadingSpinner"><BeatLoader color={"#045AA1"} /></div>);
    }

    return (
      <div>
        <Formik
          initialValues={companyOptions}
          validationSchema={OptionsSchema}
          onSubmit={values => {
            setCompanyOptions(values);
            saveForm(values);
          }}
        >
          {({ errors, values, touched }) => (
            <div>
              <div style={styles.optionsContainer}>
                <div style={{ display: 'flex', gap: '32px' }}>
                  <span style={styles.dot}></span>
                  <span style={styles.dot}></span>
                  <span style={styles.dot}></span>
                </div>
                <img src="./../assets/computerIcon.png" height={153.59}></img>
                <div style={{ color: 'white' }}>
                  <div style={{ color: 'white', fontWeight: 'bold', fontSize: '28px' }}>
                    {props.createCompany ? 'Create Company' : `Edit Company: ${companyOptions.code}`}
                  </div>
                  <span>Configurations available are listed below</span>
                </div>
                <div style={{ width: '320px' }}></div>
              </div>

              <div className='row' style={styles.configurationContainer}>
                <div className='col-md-4' style={styles.configOptionsContainer}>
                  <div style={styles.configOptionsContainerGroup}>
                    <span>
                      <span style={{ fontWeight: 'bold', fontSize: '26px' }}>
                        Company Data
                      </span>
                      <Bubble helpText={"Our application uses your company info to personalize the forms. Enter your company information as you want it to appear on your forms here."} width={300}></Bubble>
                    </span>
                    <hr style={{ color: 'var(--bs-primary)', borderTop: '2px solid' }} />

                    <div className="card">
                      <div className="card-body">
                        <Form>
                          <Field errors={errors} name="interpolationData.CompanyName" component={FormikTextInput} formiklabel="Display Name" />
                          <Field errors={errors} name="interpolationData.CompanyEmail" component={FormikEmailInput} style={{ width: '100%' }} formiklabel="Email Address" />
                          <Field errors={errors} name="interpolationData.CompanyPhone" component={FormikTextInput} formiklabel="Phone" />
                          <Field errors={errors} name="interpolationData.CompanyWebsite" component={FormikTextInput} formiklabel="Website" />
                          <Field errors={errors} name="interpolationData.CompanyPortal" component={FormikTextInput} formiklabel="Portal" />
                          <Field errors={errors} name="interpolationData.CompanyAddress" component={FormikTextInput} formiklabel="Address" />
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-md-4' style={styles.configOptionsContainer}>
                  <div style={styles.configOptionsContainerGroup}>
                    <span style={{ fontWeight: 'bold', fontSize: '26px' }}>Email</span>
                    <hr style={{ color: 'var(--bs-primary)', borderTop: '2px solid' }} />

                    <div className="card">
                      <div className="card-body">
                        <Form>
                          <Field errors={errors} name="emailReply" component={FormikEmailInput} style={{ width: '100%' }} formiklabel="Email Reply Address">
                            <Bubble helpText={"The email address used as the TO field when your client replies to their checklist."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="emailChecklistStyle" component={FormikSelect} formiklabel="Checklist Style" values={[
                            { text: "N/A", value: "" },
                            { text: "Bullet", value: "bullet" },
                            { text: "Checkbox", value: "checkbox" },
                            { text: "Circle", value: "circle" }
                          ]} />

                          <br></br>
                          <h4>Optional settings</h4>
                          <hr></hr>

                          <Field errors={errors} name="email" component={FormikEmailInput} style={{ width: '100%' }} formiklabel="Email From Address" />

                          <Field errors={errors} name="emailProvider" component={FormikSelect} formiklabel="Email Provider" values={[
                            { text: "Google", value: "gmail" },
                            { text: "Yahoo", value: "yahoo" },
                            { text: "Outlook", value: "outlook365" }
                          ]} />

                          <Field errors={errors} name="emailKey" component={FormikPasswordInput} formiklabel="Email App Password">
                            <Bubble helpText={"An App Password is a email provider generated password that allows applications to send emails on your behalf."} width={300}></Bubble>
                          </Field>
                        </Form>
                      </div>
                    </div>
                  </div>

                  <div style={styles.configOptionsContainerGroup}>
                    <span>
                      <span style={{ fontWeight: 'bold', fontSize: '26px' }}>
                        Extra Features
                      </span>
                      <Bubble helpText={"Our application has some additional features that may be turned on as needed."} width={300}></Bubble>
                    </span>
                    <hr style={{ color: 'var(--bs-primary)', borderTop: '2px solid' }} />

                    <div className="card">
                      <div className="card-body">
                        <Form>
                          <Field errors={errors} name="extraFeatures.sharableStatus" component={FormikCheckBoxSingle} formiklabel="Sharable Status">
                            <Bubble helpText={"This feature allows you to track the status of a tax return and share that status with your clients."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="extraFeatures.assignAdmins" component={FormikCheckBoxSingle} formiklabel="Assignable Returns">
                            <Bubble helpText={"This feature allows you to assign and track which preparer is associated with a given client."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="extraFeatures.submissionTagging" component={FormikCheckBoxSingle} formiklabel="Submission Tagging">
                            <Bubble helpText={"This feature allows you to tag submission answers and/or required documents."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="extraFeatures.logoSize" component={FormikSliderNumber} formikrangemin={150} formikrangemax={500} formikrangestep={5} formiklabel="Logo Height">
                            <Bubble helpText={"This feature allows you to alter the height of your logo that appears on your forms."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="extraFeatures.messaging" component={FormikCheckBoxSingle} formiklabel="Client Messaging" disabled={true}></Field>
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-md-4' style={styles.configOptionsContainer}>
                  <div style={styles.configOptionsContainerGroup}>
                    <span>
                      <span style={{ fontWeight: 'bold', fontSize: '26px' }}>
                        CRM Integration
                      </span>
                      <Bubble helpText={"Our application also provides a CRM, to integrate with this CRM fill in the details below."} width={300}></Bubble>
                    </span>
                    <hr style={{ color: 'var(--bs-primary)', borderTop: '2px solid' }} />

                    <div className="card">
                      <div className="card-body">
                        <Form>
                          <Field errors={errors} name="highLevel.enabled" component={FormikCheckBoxSingle} formiklabel="Enabled">
                            <Bubble helpText={"This allows you to enable the integration to our provided CRM."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="highLevel.locationId" component={FormikTextInput} formiklabel="Location ID">
                            <Bubble helpText={"The location ID that is assigned to your CRM account."} width={300}></Bubble>
                          </Field>

                          <Field errors={errors} name="highLevel.privateIntegrationKey" component={FormikPasswordInput} formiklabel="Private Integration Key">
                            <Bubble helpText={"The private integration key generated within your CRM account."} width={300}></Bubble>
                          </Field>
                          
                          {values.highLevel?.enabled && values.highLevel?.locationId && values.highLevel?.privateIntegrationKey && (
                            <button 
                              type="button" 
                              className="btn btn-success mt-3" 
                              onClick={() => finalizeIntegration(values)}
                              disabled={finalizing}
                            >
                              {finalizing ? 'Finalizing...' : 'Finalize Integration'}
                            </button>
                          )}
                        </Form>
                      </div>
                    </div>
                  </div>

                  <div style={styles.configOptionsContainerGroup}>
                    <span style={{ fontWeight: 'bold', fontSize: '26px' }}>Stripe Settings</span>
                    <hr style={{ color: 'var(--bs-primary)', borderTop: '2px solid' }} />

                    <div className="card">
                      <div className="card-body">
                        <Form>
                          <Field errors={errors} name="isSubscribed" component={FormikCheckBoxSingle} formiklabel="Is Subscribed" />
                          
                          <Field errors={errors} name="stripeCustomerId" component={FormikTextInput} formiklabel="Stripe Customer ID" />
                          
                          <Field errors={errors} name="stripeSubscription" component={FormikTextInput} formiklabel="Stripe Subscription ID" />
                          
                          <Field errors={errors} name="stripeSubscriptionStatus" component={FormikSelect} formiklabel="Subscription Status" values={[
                            { text: "Active", value: "active" },
                            { text: "Canceled", value: "canceled" },
                            { text: "Incomplete", value: "incomplete" },
                            { text: "Past Due", value: "past_due" },
                            { text: "Trialing", value: "trialing" },
                            { text: "Unpaid", value: "unpaid" }
                          ]} />
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Form style={{ height: 100, display: 'flex', justifyContent: 'center' }}>
                <button className="btn btn-sm btn-primary mt-3" type="submit" style={{ height: '50px', width: '124px' }}>Save</button>
              </Form>
            </div>
          )}
        </Formik>
      </div>
    );
  }

  return render();
}

const styles = {
  optionsContainer: {
    height: 'auto',
    padding: '20px',
    backgroundColor: 'var(--bs-dark-blue)',
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: '40px'
  },
  dot: {
    height: '48px',
    width: '48px',
    backgroundColor: 'var(--bs-tope)',
    borderRadius: '50%',
    display: 'inline-block',
  },
  configurationContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: '16px',
    padding: '40px'
  },
  configOptionsContainer: {
    width: '398px',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  configOptionsContainerGroup: {
    marginBottom: 16
  }
}

export default SystemCompanyOptions;